import { Injectable, inject } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, EMPTY, filter, map, switchMap } from 'rxjs';

import { UserConfigsStoreFacade } from './user-configs-store.facade';
import * as UserConfigsActions from './user-configs.actions';
import { CustomerIdTypes } from '../../../definitions/enums/customers/customer-id-types.enum';
import { CountryConfig } from '../../../definitions/interfaces/country-config/country-config';
import { CustomerConfig } from '../../../definitions/interfaces/customer/customer-config';
import { CountrySetupService } from '../../../services/country/country-setup.service';
import { CustomerService } from '../../../services/customer/customer.service';

@Injectable()
export class UserConfigsEffects {
  private readonly actions$ = inject(Actions);
  private readonly customerService = inject(CustomerService);
  private readonly countryService = inject(CountrySetupService);
  private readonly userConfigsFacade = inject(UserConfigsStoreFacade);

  public loadCurrentCustomerConfig$ = createEffect(() => this.actions$.pipe(
    ofType(UserConfigsActions.replenishmentStarted),
    filter(() => !!this.userConfigsFacade.currentCustomerErpRefSignal()),
    switchMap(() =>
      this.customerService.getCustomerConfig(this.userConfigsFacade.currentCustomerErpRefSignal()!, CustomerIdTypes.erpNumber)
        .pipe(
          map((currentCustomerConfig: CustomerConfig) =>
            UserConfigsActions.currentCustomerConfigLoaded({ currentCustomerConfig }),
          )
        )
    ))
  );

  public loadCurrentCountryConfig$ = createEffect(() => this.actions$.pipe(
    ofType(UserConfigsActions.replenishmentStarted),
    switchMap(() =>
      this.countryService.getCountryConfig(this.userConfigsFacade.currentCountryCodeSignal())
        .pipe(
          map((currentCountryConfig: CountryConfig) =>
            UserConfigsActions.currentCountryConfigLoaded({ currentCountryConfig }),
          )
        )
    ))
  );

  public loadSuperAdminCountries = createEffect(() => this.actions$.pipe(
    ofType(UserConfigsActions.userDataLoaded),
    filter(() => this.userConfigsFacade.isSuperAdminSignal()),
    switchMap(() =>
      this.countryService.getAllCountryConfigs().pipe(
        map((configurations: CountryConfig[]) => {
          const superAdminCountries = configurations.map((configuration) => configuration.country);
          return UserConfigsActions.superAdminCountriesLoaded({ superAdminCountries });
        }),
        catchError(() => EMPTY)
      ))
  ));
}
